// frontend/src/pages/QuizPage.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuizPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [trialMode, setTrialMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  async function loadQuestions() {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        navigate('/login');
        return;
      }

      const res = await axios.get(`${API}/api/questions?section=${section}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(res.data.questions);
      setTrialMode(res.data.trialMode);
      setLoading(false);
    } catch (err) {
      if (err.response?.data?.error === 'paywall') {
        // Redirect to pricing instead of showing blank screen
        navigate('/pricing');
      } else {
        setError('Failed to load questions. Please try again.');
        setLoading(false);
      }
    }
  }

  async function handleAnswer(option) {
    setSelected(option);
    setShowExplanation(true);
    if (option === questions[current].answer) {
      setScore(s => s + 1);
    }
  }

  async function handleNext() {
    if (current + 1 >= questions.length) {
      // Trial complete — mark it done
      if (trialMode) {
        const { data: { session } } = await supabase.auth.getSession();
        await axios.post(`${API}/api/questions/complete-trial`, {}, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
      }
      setFinished(true);
    } else {
      setCurrent(c => c + 1);
      setSelected(null);
      setShowExplanation(false);
    }
  }

  if (loading) return <div style={styles.center}>Loading questions...</div>;

  if (error) return (
    <div style={styles.center}>
      <h2>⚠️ Something went wrong</h2>
      <p>{error}</p>
      <button onClick={loadQuestions} style={styles.btn}>Try Again</button>
      <button onClick={() => navigate('/')} style={{ ...styles.btn, background: '#888', marginLeft: '12px' }}>
        Back to Dashboard
      </button>
    </div>
  );

  if (finished) return (
    <div style={styles.center}>
      <h2>🎉 {trialMode ? 'Trial Complete!' : 'Quiz Complete!'}</h2>
      <p style={{ fontSize: '24px' }}>Score: {score} / {questions.length}</p>
      {trialMode && (
        <>
          <p>You've completed your 10 free questions. Unlock the full question bank!</p>
          <button onClick={() => navigate('/pricing')} style={styles.btn}>
            View Pricing Plans
          </button>
        </>
      )}
      {!trialMode && (
        <button onClick={() => navigate('/')} style={styles.btn}>Back to Dashboard</button>
      )}
    </div>
  );

  if (!questions.length) return (
    <div style={styles.center}>
      <h2>No questions found</h2>
      <button onClick={() => navigate('/')} style={styles.btn}>Back to Dashboard</button>
    </div>
  );

  const q = questions[current];

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '40px 20px' }}>
      {/* Progress bar */}
      <div style={{ background: '#e0e0e0', borderRadius: '8px', height: '8px', marginBottom: '24px' }}>
        <div style={{ background: '#1E3A5F', height: '100%', borderRadius: '8px',
          width: `${((current) / questions.length) * 100}%`, transition: 'width 0.3s' }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ color: '#555' }}>Question {current + 1} of {questions.length}</span>
        {trialMode && <span style={{ color: '#C05000', fontWeight: 'bold' }}>🔓 Free Trial</span>}
      </div>

      <div style={{ background: '#fff', borderRadius: '16px', padding: '32px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
        <p style={{ fontSize: '18px', fontWeight: '500', marginBottom: '24px', lineHeight: '1.6' }}>
          {q.question}
        </p>

        {Object.entries(q.options).map(([key, val]) => {
          let bg = '#f8f9fa';
          if (selected) {
            if (key === q.answer) bg = '#D4EDDA';
            else if (key === selected && key !== q.answer) bg = '#F8D7DA';
          }
          return (
            <div key={key}
              onClick={() => !selected && handleAnswer(key)}
              style={{ background: bg, border: '2px solid #e0e0e0', borderRadius: '10px',
                padding: '16px', marginBottom: '12px', cursor: selected ? 'default' : 'pointer',
                transition: 'all 0.2s' }}
            >
              <strong style={{ color: '#1E3A5F', marginRight: '12px' }}>{key}.</strong> {val}
            </div>
          );
        })}

        {showExplanation && (
          <div style={{ background: '#EBF5FB', border: '1px solid #AED6F1', borderRadius: '10px',
                        padding: '16px', marginTop: '16px' }}>
            <strong>💡 Explanation:</strong> {q.explanation}
          </div>
        )}
      </div>

      {selected && (
        <button onClick={handleNext} style={styles.btn}>
          {current + 1 >= questions.length ? 'See Results' : 'Next Question →'}
        </button>
      )}
    </div>
  );
}

const styles = {
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', minHeight: '80vh', textAlign: 'center', padding: '20px' },
  btn: { padding: '14px 32px', background: '#1E3A5F', color: '#fff',
         border: 'none', borderRadius: '10px', fontSize: '16px', cursor: 'pointer', marginTop: '16px' }
};
